import axios from 'axios';
import { md5 } from 'js-md5';
import { authSession } from '@/features/auth/stores/auth.store';
import { getJti } from './jwt';
import { getDeviceId, getUserAgent } from './device';
import { unwrapMedis, type MedisEnvelope } from './envelope';
import { endpointUrls, proxifyMedisUrl } from './endpointUrls';
import { endpoints } from '@/lib/api/endpoints';

/**
 * MEDIS session manager — the web port of the mobile `AzureAccessCodeService`.
 *
 * MEDIS (Azure) endpoints don't use the JWT directly. Regular requests carry:
 *   - `x-gral-accesscode`:    a short-lived session code from `POST /NewSession`
 *   - `x-gral-accessvalidator`: md5(accessCode + jwt.jti)
 *
 * The raw JWT (`x-gral-authorization`) is sent ONLY on the `/NewSession`
 * handshake (see `fetchSession`); the server rejects it on the data endpoints
 * (e.g. GetAccessList), so it must never accompany the access code.
 *
 * The access code is cached and bound to the JWT's `jti`; a token refresh
 * rotates the `jti`, which invalidates the validator and forces a new session.
 * Concurrent callers share a single in-flight `/NewSession` (single-flight) so
 * we never spend more than one handshake at a time.
 */

interface CachedSession {
  accessCode: string;
  validator: string;
  /** jti of the JWT this code was bound to. */
  boundJti: string | null;
  fetchedAt: number;
  lastUsedAt: number;
}

/** MEDIS access-code lifetime caps, mirrored from the mobile app. */
const MAX_AGE_MS = 3 * 60 * 60 * 1000; // 3 hours
const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Circuit-breaker cooldown after a failed handshake. While open, getHeaders()
 * rejects immediately WITHOUT hitting the network — this is what stops a
 * NewSession request storm when MEDIS is unreachable (CORS, outage, DNS).
 */
const FAILURE_COOLDOWN_MS = 10_000;

/** Thrown when the MEDIS session can't be established; flagged non-retryable. */
export class MedisUnavailableError extends Error {
  readonly isMedisUnavailable = true;
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'MedisUnavailableError';
  }
}

export interface MedisHeaders {
  'x-gral-accesscode': string;
  'x-gral-accessvalidator': string;
}

/**
 * Bare client used only for `/NewSession` so the handshake never recurses
 * through the MEDIS interceptors. No baseURL — the URL is resolved through the
 * dynamic endpoint registry per request.
 */
const bareMedis = axios.create({
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  timeout: 30_000,
});

class MedisSessionManager {
  private cache: CachedSession | null = null;
  private inFlight: Promise<CachedSession> | null = null;
  private lastSessionCode: string | null = null;
  /** Circuit breaker: while now < failedUntil, fail fast without a network call. */
  private failedUntil = 0;
  private lastError: MedisUnavailableError | null = null;

  /** Returns the three headers every MEDIS request needs, refreshing the session if required. */
  async getHeaders(): Promise<MedisHeaders> {
    const token = authSession.getAccessToken();
    if (!token) {
      throw new Error('MEDIS: no access token — user is not authenticated.');
    }

    const session = await this.ensureSession(token);
    session.lastUsedAt = Date.now();

    return {
      'x-gral-accesscode': session.accessCode,
      'x-gral-accessvalidator': session.validator,
    };
  }

  /** Force the next call to re-handshake (e.g. after a 401 from a MEDIS endpoint). */
  invalidate(): void {
    this.cache = null;
    this.inFlight = null;
    // Clear the breaker so an explicit invalidate can retry immediately.
    this.failedUntil = 0;
    this.lastError = null;
  }

  private isValid(token: string): boolean {
    if (!this.cache) return false;
    const now = Date.now();
    if (now - this.cache.fetchedAt > MAX_AGE_MS) return false;
    if (now - this.cache.lastUsedAt > IDLE_TIMEOUT_MS) return false;
    // jti binding: a token refresh rotates jti and breaks the validator.
    return this.cache.boundJti === getJti(token);
  }

  private ensureSession(token: string): Promise<CachedSession> {
    if (this.isValid(token)) return Promise.resolve(this.cache!);
    if (this.inFlight) return this.inFlight;

    // Circuit open: a recent handshake failed — fail fast, no network call.
    if (Date.now() < this.failedUntil) {
      return Promise.reject(this.lastError ?? new MedisUnavailableError('MEDIS indisponibil.'));
    }

    this.inFlight = this.fetchSession(token)
      .then((session) => {
        this.cache = session;
        this.lastSessionCode = session.accessCode;
        this.failedUntil = 0;
        this.lastError = null;
        return session;
      })
      .catch((error) => {
        // Trip the breaker so subsequent calls fail fast for a cooldown window.
        this.failedUntil = Date.now() + FAILURE_COOLDOWN_MS;
        this.lastError =
          error instanceof MedisUnavailableError
            ? error
            : new MedisUnavailableError('Serviciul medical este momentan indisponibil.', error);
        throw this.lastError;
      })
      .finally(() => {
        this.inFlight = null;
      });

    return this.inFlight;
  }

  private async fetchSession(token: string): Promise<CachedSession> {
    const body = {
      ...(this.lastSessionCode ? { OldSessionCode: this.lastSessionCode } : {}),
      DeviceMode: false, // web client (mobile sends true)
      DeviceUserAgent: getUserAgent(),
      DeviceID: getDeviceId(),
    };

    // Resolve the NewSession URL through the dynamic registry (proxied in dev).
    await endpointUrls.ensureLoaded();
    const url = proxifyMedisUrl(endpointUrls.resolve('newsession', endpoints.medis.newSession));

    const { data } = await bareMedis.post<MedisEnvelope<{ AccessCode: string }>>(url, body, {
      headers: { 'x-gral-authorization': token },
    });

    const accessCode = unwrapMedis(data)?.AccessCode;
    if (!accessCode) {
      throw new Error('MEDIS: NewSession returned no AccessCode.');
    }

    const jti = getJti(token);
    const validator = md5(accessCode + (jti ?? ''));

    return {
      accessCode,
      validator,
      boundJti: jti,
      fetchedAt: Date.now(),
      lastUsedAt: Date.now(),
    };
  }
}

export const medisSession = new MedisSessionManager();
